import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { CommonCodeColumn } from 'src/utils/grid/GridColumnContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { MST3040_R01UsageDto } from './Dto_MST3040_R01';
import { useMST3040_R01Store } from './Store_MST3040_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'MST3040_R01-usage-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST3040_R01UsageDto> | null>;
	defaultMST3040_R01UsageRow: MST3040_R01UsageDto;
}

export const UsageTable_MST3040_R01 = (props: Props) => {
	const { gridRef, defaultMST3040_R01UsageRow } = props;

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		MST3040_R01UsageList,
		setMST3040_R01UsageList,
		selectedMST3040_R01Usage,
		setSelectedMST3040_R01Usage,
		initSelectedMST3040_R01Usage,
		updateMST3040_R01UsageList,
		usageColumnState,
		setUsageColumnState,
		usageScrollTop, setUsageScrollTop,
		originalMST3040_R01UsageList,
	} = useMST3040_R01Store();

	const { materialIntypeList } = useCommonCodeStore();

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'childItemcd',
		gridData: MST3040_R01UsageList,
		defaultGridData: defaultMST3040_R01UsageRow,
		selectedRow: selectedMST3040_R01Usage,
		originalGridData: originalMST3040_R01UsageList,
		updateRow: (index: number, data: MST3040_R01UsageDto) =>
			updateMST3040_R01UsageList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'childItemcd')(params), []);

	const baseColumnDefs: ColDef<MST3040_R01UsageDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '자품목코드', colId: 'childItemcd', field: 'childItemcd', width: 95,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'childItemcd'),
		},
		{
			headerName: '품명', colId: 'childItemnm', field: 'childItemnm', width: 210,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'childItemnm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 210,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
		},
		{
			headerName: '자재공급방법', colId: 'materialIntype', field: 'materialIntype', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'materialIntype'),
			...CommonCodeColumn.options(materialIntypeList, false, undefined, undefined),
		},
		{
			headerName: 'Bom소요량', colId: 'childItemUsage', field: 'childItemUsage', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'childItemUsage'),
			editable: false,
		},
		{
			headerName: '좌표정보소요량', colId: 'coordinateUsage', field: 'coordinateUsage', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'coordinateUsage'),
			editable: false,
		},
		{
			headerName: '소요량 차이', colId: 'usageDifference', field: 'usageDifference', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'usageDifference'),
			editable: false,
		},
	], [getCellStyle, materialIntypeList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<MST3040_R01UsageDto>[] = useMemo(() => {
		if (usageColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST3040_R01UsageDto>({
			baseColumnDefs,
			columnState: usageColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [usageColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<MST3040_R01UsageDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedMST3040_R01Usage?.childItemcd === rowData.childItemcd;
		setSelectedMST3040_R01Usage(isSelected ? undefined : rowData);
	}, [selectedMST3040_R01Usage, setSelectedMST3040_R01Usage]);

	const pinnedBottomRow = useMemo(() => {
		const differenceQty = MST3040_R01UsageList.reduce(
			(sum, item) => sum + (Number(item.usageDifference) || 0), 0,
		);
		return [{
			childItemcd: `${MST3040_R01UsageList.length.toLocaleString()}건`,
			usageDifference: differenceQty,
		}];
	}, [MST3040_R01UsageList]);

	return (
		<GridContextWrapper<MST3040_R01UsageDto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			enableCellCalculation
			setPaddedGridData={setMST3040_R01UsageList}
			initSelected={initSelectedMST3040_R01Usage}
			defaultRowData={defaultMST3040_R01UsageRow}
			baseColumnDefs={usageColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setUsageColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<MST3040_R01UsageDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<MST3040_R01UsageDto>({
					idKey: 'childItemcd',
					selectedList: selectedMST3040_R01Usage ? [selectedMST3040_R01Usage.childItemcd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setUsageColumnState}
				scrollTop={usageScrollTop}
				setScrollTop={setUsageScrollTop}
			/>
		</GridContextWrapper>
	);
};
